import React, { useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { useGetImageThemeQuery } from "./services/GetImageThemesApiSlice";
import DataLoader from "../../components/custom-data-loader/DataLoader";
import { setImageThemes } from "./services/BabySizeImageDataSlice";
import BabySizeImageCard from "./BabySizeImageCard";
import BabySizeImageCardSkeleton from "./BabySizeImageCardSkeleton";
import {
  CHILDLIVINGSTATUSATBIRTH_STATUS_ID_MAPPING,
  IMAGE_THEME,
} from "../../utility/constants/common-constants";
import {
  ImageTheme,
  ImageWeekInfo,
} from "../../utility/interfaces/babyWeekImageDataInterface";

interface BabySizeImageWidgetProps {
  handleViewAll: () => void;
}

const BabySizeImageWidget: React.FC<BabySizeImageWidgetProps> = ({
  handleViewAll,
}) => {
  const dispatch = useAppDispatch();

  const { livingStatusAtBirthId, actualDeliveryDate } = useAppSelector(
    (state) => state.patientOnboardingStatus.loginWorkflowInfo
  );

  const isBabyDelivered =
    livingStatusAtBirthId ===
      CHILDLIVINGSTATUSATBIRTH_STATUS_ID_MAPPING.living &&
    actualDeliveryDate !== null;

  const patientProfileId = useAppSelector(
    (state) => state.patientProfileDetails.patientProfileInfo.patientProfileId
  );

  const patientPreferredLang = useAppSelector(
    (state) => state.patientOnboardingStatus.preferredLanguage
  );

  const patientPreferences =
    useAppSelector((state) => state.patientPreferences.patientPreferences) ||
    [];

  // Get preferred imageThemeId from preferences
  const preferredThemeId = patientPreferences.find(
    (pref) => pref.categoryCode === IMAGE_THEME
  )?.preferenceIdentifier;

  const {
    data: imageThemeData,
    error: imageThemeError,
    isFetching: imageThemeLoading,
  } = useGetImageThemeQuery({
    patientId: patientProfileId,
    preferredLanguage: patientPreferredLang,
  });

  useEffect(() => {
    if (imageThemeData) {
      dispatch(setImageThemes(imageThemeData));
    }
  }, [dispatch, imageThemeData]);

  return (
    <DataLoader
      isLoading={imageThemeLoading}
      error={imageThemeError}
      data={imageThemeData}
      loadingComponent={<BabySizeImageCardSkeleton />}
      emptyComponent={<Typography>No images found.</Typography>}
      errorComponent={
        <Typography color="error">Error Loading Baby Week Data</Typography>
      }
    >
      {() => {
        const currentWeek = imageThemeData.currentPregnancyWeek;
        const selectedTheme =
          imageThemeData?.imageThemes.find(
            (theme: ImageTheme) =>
              theme.imageThemeId.toString() === preferredThemeId
          ) ??
          imageThemeData?.imageThemes.find(
            (theme: ImageTheme) => theme.isDefaultTheme
          );

        const currentWeekData =
          selectedTheme?.patientImageWeekInfo.find(
            (week: ImageWeekInfo) => week.pregnancyWeekId === currentWeek
          ) ??
          selectedTheme?.patientImageWeekInfo.find(
            (week: ImageWeekInfo) => week.pregnancyWeekId === 40
          );

        return (
          <Box>
            {!isBabyDelivered && currentWeekData && (
              <BabySizeImageCard
                onViewAll={handleViewAll}
                data={currentWeekData}
              />
            )}
          </Box>
        );
      }}
    </DataLoader>
  );
};

export default BabySizeImageWidget;
